/**
 * User blocklist schemas -- client-side validation for /api/blocklist/*.
 *
 * The user blocklist is a per-user list of phone numbers that should never
 * receive messages from this user account. It mirrors the upstream sendSMS
 * blocklist (synced via cron) plus our own local additions and GDPR opt-outs.
 *
 * Server is authoritative. Client validates upfront for fast UX feedback;
 * backend re-validates everything on the request handler.
 *
 * Schemas exported (request bodies):
 *   - addBlocklistEntrySchema      POST   /api/blocklist
 *   - bulkImportBlocklistSchema    POST   /api/blocklist/bulk
 *   - listBlocklistRequestSchema   GET    /api/blocklist?...
 *
 * Threat model V136-V145:
 *   V136 enumeration via timing attack
 *        -> indexed lookup, response time bounded by page_size cap
 *   V137 bulk import flooding
 *        -> MAX_BULK_IMPORT cap (10k entries) enforced here + rate limit on route
 *   V138 cross-user blocklist leak
 *        -> tenant isolation in queries (always WHERE user_id = ?)
 *   V139 ELIMINATED: GDPR unsubscribe handled by sendSMS (ros.ms shortener)
 *   V140 sync race condition
 *        -> D1 conditional UPDATE on sync_status (cas-style)
 *   V141 sendSMS API key abuse via blocklist endpoints
 *        -> reuse rate limit infrastructure (rl:blocklist_*)
 *   V142 phone number normalization bypass
 *        -> phoneSchema (libphonenumber-js) before storage
 *   V143 soft-delete bypass via re-add
 *        -> partial unique index handles it (deleted_at IS NULL)
 *   V144 cron starvation by huge blocklist sync
 *        -> CRON_SYNC_BATCH_SIZE = 1000 entries per tick
 *   V145 audit log gap on bulk operations
 *        -> 1 audit entry per bulk op, with metadata.count + metadata.sample
 *
 * Types inferred via z.infer<typeof ...>.
 */

import { z } from "zod";

import { phoneSchema, uuidSchema } from "./primitives";

// -----------------------------------------------------------
// Limits (mirror server-side enforcement)
// -----------------------------------------------------------

export const BLOCKLIST_LIMITS = {
  /** Max chars in optional notes field. */
  NOTES_MAX: 500,
  /** Max entries per single bulk import call (V137 protection). */
  MAX_BULK_IMPORT: 10000,
  /** Default UI page size for list endpoint. */
  PAGE_SIZE_DEFAULT: 50,
  /** Max UI page size (cap to prevent timing-based enumeration V136). */
  PAGE_SIZE_MAX: 200,
  /** Max chars for search query (phone substring). */
  SEARCH_MAX: 20,
  /** Cron sync batch size to avoid CPU starvation (V144). */
  CRON_SYNC_BATCH_SIZE: 1000,
} as const;

// -----------------------------------------------------------
// Enums
// -----------------------------------------------------------

/** How an entry landed in the blocklist. Used for UX badges + audit. */
export const BLOCKLIST_SOURCES = [
  "manual",
  "import",
  "sync_pull",
  "gdpr_unsubscribe",
  "campaign_dlr",
  /** STOP/unsubscribe received via MO on any channel (V451). */
  "mo_unsubscribe",
] as const;
export type BlocklistSource = (typeof BLOCKLIST_SOURCES)[number];

/** Sync state with upstream sendSMS. */
export const BLOCKLIST_SYNC_STATUSES = [
  "synced",
  "pending_push",
  "pending_delete",
  "conflict",
] as const;
export type BlocklistSyncStatus = (typeof BLOCKLIST_SYNC_STATUSES)[number];

// -----------------------------------------------------------
// Request schemas
// -----------------------------------------------------------

/** POST /api/blocklist -- add a single phone to blocklist. */
export const addBlocklistEntrySchema = z.object({
  phone_e164: phoneSchema,
  notes: z.string().max(BLOCKLIST_LIMITS.NOTES_MAX).nullish(),
});
export type AddBlocklistEntryInput = z.infer<typeof addBlocklistEntrySchema>;

/** POST /api/blocklist/bulk -- bulk import. */
export const bulkImportBlocklistSchema = z.object({
  phones: z
    .array(phoneSchema)
    .min(1)
    .max(BLOCKLIST_LIMITS.MAX_BULK_IMPORT),
  notes_default: z.string().max(BLOCKLIST_LIMITS.NOTES_MAX).nullish(),
});
export type BulkImportBlocklistInput = z.infer<typeof bulkImportBlocklistSchema>;

/** GET /api/blocklist -- list with filters + pagination. */
export const listBlocklistRequestSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce
    .number()
    .int()
    .min(1)
    .max(BLOCKLIST_LIMITS.PAGE_SIZE_MAX)
    .default(BLOCKLIST_LIMITS.PAGE_SIZE_DEFAULT),
  /** Phone substring search (digits only, max 20 chars). */
  search: z.string().max(BLOCKLIST_LIMITS.SEARCH_MAX).nullish(),
  source: z.enum(BLOCKLIST_SOURCES).nullish(),
  sync_status: z.enum(BLOCKLIST_SYNC_STATUSES).nullish(),
});
export type ListBlocklistRequest = z.infer<typeof listBlocklistRequestSchema>;

/** DELETE /api/blocklist/:id -- path param validation. */
export const blocklistEntryIdSchema = z.object({
  id: uuidSchema,
});

// -----------------------------------------------------------
// Response DTOs (what server returns to client)
// -----------------------------------------------------------

/**
 * Single blocklist entry as exposed to the frontend.
 * NEVER includes: org_id, sendsms_id, sendsms_phone_raw, deleted_at, user_id.
 * (Tenant isolation V138 + audit field minimization.)
 */
export type BlocklistEntryDto = {
  id: string;
  phone_e164: string;
  source: BlocklistSource;
  notes: string | null;
  sync_status: BlocklistSyncStatus;
  /** Unix seconds. Null until first successful sync. */
  last_synced_at: number | null;
  /** Unix seconds. */
  created_at: number;
  /** Unix seconds. */
  updated_at: number;
};

/** Paginated list response. */
export type ListBlocklistResponseDto = {
  entries: BlocklistEntryDto[];
  total: number;
  page: number;
  page_size: number;
};

/** Bulk import response: counts of what happened. */
export type BulkImportBlocklistResponseDto = {
  /** Successfully added (will be pushed upstream by cron). */
  added: number;
  /** Skipped because already in blocklist (active or soft-deleted recently). */
  duplicates: number;
  /** Rejected because phone failed validation. */
  invalid: number;
  /** Sample of invalid phones (max 10) for UI feedback. */
  invalid_sample: string[];
};

/** Manual sync trigger response. */
export type SyncBlocklistResponseDto = {
  /** Total entries on sendSMS upstream after sync. */
  upstream_total: number;
  /** New entries pulled from upstream into our cache. */
  pulled: number;
  /** Entries pushed from our cache to upstream. */
  pushed: number;
  /** Entries deleted from upstream (we had pending_delete). */
  deleted_upstream: number;
  /** Conflicts detected (manual review needed). */
  conflicts: number;
  /** Unix seconds when sync started. */
  synced_at: number;
};
